import type { Cost, SetDef, UnitDef } from "./types.js";
import type { Rng } from "./rng.js";
import { weightedIndex } from "./rng.js";
import type { MatchModifiers } from "./cards.js";

/** Pool compartilhado da sala: cópias restantes por linha. */
export type Pool = Map<string, number>;

export function createPool(set: SetDef): Pool {
  const pool: Pool = new Map();
  for (const u of set.units) pool.set(u.id, set.rules.poolCopies[u.cost - 1]!);
  return pool;
}

export function takeFromPool(pool: Pool, unitId: string, n = 1): void {
  const left = pool.get(unitId) ?? 0;
  if (left < n) throw new Error(`Pool sem cópias de ${unitId}`);
  pool.set(unitId, left - n);
}

export function returnToPool(pool: Pool, unitId: string, n = 1): void {
  pool.set(unitId, (pool.get(unitId) ?? 0) + n);
}

export function shopOdds(set: SetDef, level: number): readonly number[] {
  const odds = set.rules.shopOdds[String(level)];
  if (!odds) throw new Error(`Sem odds para nível ${level}`);
  return odds;
}

export interface RollContext {
  set: SetDef;
  pool: Pool;
  level: number;
  rng: Rng;
  mods?: Pick<MatchModifiers, "shopTypeChance" | "shopOwnedChance"> & { unlockedLines?: string[] };
  /** linhas que o jogador já possui (banco + tabuleiro) — para o Poké Radar */
  owned?: ReadonlySet<string>;
}

/**
 * Sorteia uma loja nova. As unidades sorteadas SAEM do pool (como no TFT);
 * o chamador devolve as não compradas com `returnToPool` ao rerrolar.
 *
 * Ordem por slot:
 *  1. Sorteia o custo pelas odds do nível (se o custo estiver esgotado, cai para
 *     o custo menor disponível).
 *  2. Poké Radar: com chance `shopOwnedChance`, restringe a linhas já possuídas.
 *  3. Energias: para cada tipo, com chance `shopTypeChance[tipo]`, restringe ao
 *     tipo (só se houver candidato). A primeira energia que ativar vence.
 *  4. Sorteia a linha ponderada pelas cópias restantes.
 */
export function rollShop(ctx: RollContext): (string | null)[] {
  const { set, pool, level, rng } = ctx;
  const odds = shopOdds(set, level);
  const slots: (string | null)[] = [];

  for (let i = 0; i < set.rules.shopSize; i++) {
    const unit = rollSlot(ctx, odds, rng);
    if (unit) takeFromPool(pool, unit.id);
    slots.push(unit?.id ?? null);
  }
  return slots;
}

/** A linha pode aparecer para este jogador? (linhas `locked` só depois do Criadouro) */
export function lineAvailableTo(u: UnitDef, mods?: { unlockedLines?: string[] }): boolean {
  return !u.locked || !!mods?.unlockedLines?.includes(u.id);
}

function available(set: SetDef, pool: Pool, cost: number, mods?: RollContext["mods"]): UnitDef[] {
  return set.units.filter((u) => u.cost === cost && (pool.get(u.id) ?? 0) > 0 && lineAvailableTo(u, mods));
}

function rollSlot(ctx: RollContext, odds: readonly number[], rng: Rng): UnitDef | null {
  const { set, pool, mods, owned } = ctx;

  let costIdx = weightedIndex(rng, odds);
  let candidates: UnitDef[] = [];
  while (costIdx >= 0) {
    candidates = available(set, pool, (costIdx + 1) as Cost, mods);
    if (candidates.length) break;
    costIdx--;
  }
  if (!candidates.length) return null;

  // Poké Radar
  if (mods && mods.shopOwnedChance > 0 && owned?.size) {
    if (rng.next() < mods.shopOwnedChance) {
      const allowedCosts = new Set(odds.flatMap((p, i) => (p > 0 ? [i + 1] : [])));
      const ownedCands = set.units.filter(
        (u) => owned.has(u.id) && allowedCosts.has(u.cost) && (pool.get(u.id) ?? 0) > 0 && lineAvailableTo(u, mods),
      );
      if (ownedCands.length) candidates = ownedCands;
    }
  }

  // Energias (ordem estável por chave para ser determinístico)
  if (mods) {
    for (const trait of Object.keys(mods.shopTypeChance).sort()) {
      const chance = mods.shopTypeChance[trait]!;
      if (chance <= 0) continue;
      if (rng.next() < chance) {
        const typed = candidates.filter((u) => u.traits.includes(trait));
        if (typed.length) {
          candidates = typed;
          break;
        }
      }
    }
  }

  const idx = weightedIndex(rng, candidates.map((u) => pool.get(u.id) ?? 0));
  return idx >= 0 ? candidates[idx]! : null;
}

/** Ouro de juros + base + sequência. */
export function roundIncome(set: SetDef, gold: number, streak: number, wonPvp: boolean): number {
  const e = set.rules.economy;
  const interest = Math.min(e.interestMax, Math.floor(gold / e.interestPer));
  let streakGold = 0;
  for (const s of e.streak) if (Math.abs(streak) >= s.min) streakGold = s.gold;
  return e.baseIncome + interest + streakGold + (wonPvp ? e.pvpWinGold : 0);
}

/** Valor de venda: custo * cópias; ★2+ de custo > 1 perde 1 ouro (como no TFT). */
export function sellValue(cost: Cost, star: 1 | 2 | 3): number {
  const copies = star === 1 ? 1 : star === 2 ? 3 : 9;
  const v = cost * copies;
  return star > 1 && cost > 1 ? v - 1 : v;
}

/** Dano ao treinador ao perder um combate. */
export function trainerDamage(
  set: SetDef,
  stage: number,
  survivors: readonly { cost: Cost; star: 1 | 2 | 3 }[],
): number {
  const base = set.rules.stageDamage[String(Math.min(stage, 8))] ?? 0;
  const table = set.rules.unitDamage;
  let units = 0;
  for (const s of survivors) {
    const row = s.star === 1 ? table.star1 : s.star === 2 ? table.star2 : table.star3;
    units += row[s.cost - 1] ?? 0;
  }
  return base + units;
}
